import { useEffect, useRef, useState } from 'react';
import { Play, RotateCcw } from 'lucide-react';
import { demoDefinitions, type DemoId } from '../demos/demoDefinitions';
import { useSaveData } from '../useSaveData';

export function WorkflowPreview({ demoId }: { demoId: DemoId }) {
  const definition = demoDefinitions[demoId];
  const rootRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [nearViewport, setNearViewport] = useState(false);
  const [inViewport, setInViewport] = useState(false);
  const [ready, setReady] = useState(false);
  const [portrait, setPortrait] = useState(() => window.matchMedia('(max-width: 640px)').matches);
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const saveData = useSaveData();
  const motionAllowed = !reducedMotion && !saveData;
  const base = `/demos/${demoId}/${portrait ? 'portrait' : 'desktop'}`;

  useEffect(() => {
    const query = window.matchMedia('(max-width: 640px)');
    const update = () => setPortrait(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => setReady(false), [base]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const nearObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setNearViewport(true);
        nearObserver.disconnect();
      }
    }, { rootMargin: '420px 0px' });
    const playbackObserver = new IntersectionObserver(([entry]) => setInViewport(entry.isIntersecting), { threshold: 0.38 });
    nearObserver.observe(root);
    playbackObserver.observe(root);
    return () => {
      nearObserver.disconnect();
      playbackObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !motionAllowed || !nearViewport) return;
    if (inViewport) void video.play().catch(() => undefined);
    else video.pause();
  }, [inViewport, motionAllowed, nearViewport, portrait]);

  const replay = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    void video.play().catch(() => undefined);
  };

  return (
    <figure className="workflow-preview" ref={rootRef} data-ready={ready} data-static={!motionAllowed}>
      <div className="workflow-preview__media">
        <picture className="workflow-preview__poster">
          <source media="(max-width: 640px)" srcSet={`/demos/${demoId}/portrait-poster.webp`} />
          <img src={`/demos/${demoId}/desktop-poster.webp`} alt={definition.posterAlt} loading="lazy" decoding="async" />
        </picture>
        {nearViewport && motionAllowed && (
          <video
            key={base}
            ref={videoRef}
            muted
            loop
            playsInline
            preload="metadata"
            poster={`${base}-poster.webp`}
            aria-hidden="true"
            onCanPlay={() => setReady(true)}
          >
            <source src={`${base}.webm`} type="video/webm" />
            <source src={`${base}.mp4`} type="video/mp4" />
          </video>
        )}
        <span className="workflow-preview__badge"><Play aria-hidden="true" /> 10-second workflow</span>
      </div>
      <figcaption>
        <span>{definition.description}</span>
        {motionAllowed && <button type="button" onClick={replay}><RotateCcw aria-hidden="true" />Replay workflow<span className="sr-only">: {definition.title}</span></button>}
        {!motionAllowed && <small>Shown as a still image, because your device asks for reduced motion or less data.</small>}
      </figcaption>
    </figure>
  );
}
